import type { ReactNode } from "react";

export interface ImpersonationBannerProps {
  /** The email under which the user is signed in (their own identity). */
  signedInAs: string;
  /** Text describing who they're acting as — e.g. "Jared Cohen (Thrive id=2)". */
  actingAs: string;
  /** Optional extra context shown after a separator. */
  children?: ReactNode;
}

/**
 * ImpersonationBanner — amber warning shown when a user's federated
 * identity (email) differs from the Thrive account whose ids back the
 * downstream API calls.
 *
 * Currently only triggered on the local Path 2 (real Google) flow:
 * engineers sign in with their own @nusearch.com accounts but their
 * nu_sync.users rows carry Jared's thrive_id=2, so downstream Thrive
 * calls resolve against Jared's real records. Whether HubSpot is
 * impersonated too depends on the row shape (SOP 10, rule of
 * 2026-10-20) — rows predating it also share Jared's
 * hubspot_id=78133807; newer ones carry the engineer's own HubSpot
 * user. The caller supplies that detail via `children`; this primitive
 * makes no claim about it.
 *
 * Visual mirrors the UserBanner's amber `no-user` state but as a
 * standalone banner so it can ride above the page header / popup
 * chrome on both surfaces.
 *
 * Tokenized: `text-xs` (12px) → `text-label` (12/1.4/500 — same pixels,
 * the canonical role). The amber warn palette uses `bg-warning-muted` /
 * `text-warning` / `border-warning-strong` tokens (PR #73 round-2).
 */
export function ImpersonationBanner({
  signedInAs,
  actingAs,
  children,
}: ImpersonationBannerProps) {
  return (
    <output
      data-testid="impersonation-banner"
      className="block px-4 py-2 text-label border-b bg-warning-muted text-warning border-warning-strong flex items-center gap-2"
    >
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
        focusable="false"
      >
        <title>Warning</title>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span>
        Signed in as <strong className="font-semibold">{signedInAs}</strong> —
        acting as <strong className="font-semibold">{actingAs}</strong>.
        {children ? <> {children}</> : null}
      </span>
    </output>
  );
}
